import { Dispatch, MiddlewareAPI } from 'redux';
import { PROMISE_TIMEOUT, UNAUTHORIZED_ERROR_CODE } from 'shared/consts';
import { externalLogin, getCurrentUser, refreshToken } from 'store/user/actions';
import { getCurrentAvailableService, getServiceTimeSlots } from 'store/availableServices/actions';

/* eslint-disable max-lines-per-function */
import { GetJWTHandlerPayload, HqoClientSdk } from '@hqo/hqo-miniapp-client-sdk';
import { RootAction } from 'store/actions';
import { RootState } from 'store/reducer';
import { getBuilding } from 'store/building/actions';
import { getBuildingTheme } from 'store/theme/actions';
import { getCart } from 'store/cart/actions';
import { getMineServiceBookings } from 'store/serviceBookings/actions';
import { getMiniappSdkClient } from 'hooks/use-miniapp-sdk.hook';
import { getServiceContent } from 'store/serviceContent/actions';
import { getTransactions } from 'store/transactions/actions';
import { isActionOf } from 'typesafe-actions';
import { saveExternalConfig } from 'store/config/actions';
import { showGenericErrorNotification } from 'store/errors/actions';
import { toSerializableError } from 'store/utils/map-generic-error.util';

const failedActionsTypesWithErrorHandling = [
  getBuildingTheme.failure,
  externalLogin.failure,
  getCurrentUser.failure,
  getBuilding.failure,
  getCart.failure,
  getCurrentAvailableService.failure,
  getServiceContent.failure,
  getMineServiceBookings.failure,
  getTransactions.failure,
  getServiceTimeSlots.failure,
];

const isUnauthorized = (status: number | undefined): boolean => status === UNAUTHORIZED_ERROR_CODE;

// One SDK client for all token refreshes. Creating a client per failure (the
// previous behavior) posted a new SDK INIT to the host and attached extra
// window "message" listeners on every 401 — a burst of failures snowballed
// into a postMessage storm between the miniapp and the host until the webview
// died. The SDK has no `off()`, so the getJWTResponse handler is registered
// once and fans out to whoever is currently waiting.
let sharedSdkClient: HqoClientSdk | null = null;
let pendingJwtResolvers: Array<(token: string) => void> = [];
let jwtRefreshInFlight: Promise<string> | null = null;
let lastRefreshCompletedAt = 0;

// If the API keeps rejecting requests right after a refresh, the host is
// handing us a token the backend will not accept. Retrying just loops
// 401 → getJWT → refetch → 401 forever, so back off and surface the error.
const REFRESH_COOLDOWN_MS = 30_000;

export const resetTokenRefreshStateForTesting = (): void => {
  sharedSdkClient = null;
  pendingJwtResolvers = [];
  jwtRefreshInFlight = null;
  lastRefreshCompletedAt = 0;
};

const getSharedSdkClient = (): HqoClientSdk => {
  if (!sharedSdkClient) {
    sharedSdkClient = getMiniappSdkClient();
    sharedSdkClient.on('getJWTResponse', (data: GetJWTHandlerPayload) => {
      const resolvers = pendingJwtResolvers;
      pendingJwtResolvers = [];
      resolvers.forEach(resolve => resolve(data.token));
    });
  }

  return sharedSdkClient;
};

// Concurrent 401 failures share a single getJWT round-trip instead of each
// posting their own request to the host.
const refreshJwtToken = (): Promise<string> => {
  if (!jwtRefreshInFlight) {
    jwtRefreshInFlight = new Promise<string>((resolve, reject) => {
      let timeout: ReturnType<typeof setTimeout>;

      const resolver = (token: string): void => {
        clearTimeout(timeout);
        resolve(token);
      };

      timeout = setTimeout(() => {
        // Drop the orphaned resolver so unanswered refreshes don't accumulate
        pendingJwtResolvers = pendingJwtResolvers.filter(pendingResolver => pendingResolver !== resolver);
        reject(new Error('JWT refresh timeout'));
      }, PROMISE_TIMEOUT);

      pendingJwtResolvers.push(resolver);

      getSharedSdkClient().identity.getJWT();
    }).finally(() => {
      jwtRefreshInFlight = null;
    });
  }

  return jwtRefreshInFlight;
};

// Refresh the token and retry via refreshToken(); returns false when the
// refresh failed/timed out and the failure should be surfaced instead.
const tryRecoverFromExpiredToken = async (store: MiddlewareAPI<Dispatch<RootAction>, RootState>): Promise<boolean> => {
  try {
    const token = await refreshJwtToken();
    lastRefreshCompletedAt = Date.now();

    store.dispatch(saveExternalConfig({ authToken: `Bearer ${token}` }));
    store.dispatch(refreshToken());

    return true;
  } catch {
    return false;
  }
};

export const tokenExpiryMiddleware =
  (store: MiddlewareAPI<Dispatch<RootAction>, RootState>) =>
  (next: Dispatch<RootAction>) =>
  async (action: RootAction) => {
    // Handle failure actions (supports both { error, errorCode } and PaymentError payloads)
    if (!isActionOf(failedActionsTypesWithErrorHandling)(action)) {
      next(action);
      return;
    }

    const error = toSerializableError(action.payload.error);
    const errorStatusCode = error?.response?.statusCode;
    const isWithinRefreshCooldown = Date.now() - lastRefreshCompletedAt < REFRESH_COOLDOWN_MS;
    const canRecoverFromExpiredToken = isUnauthorized(errorStatusCode) && !isWithinRefreshCooldown;

    if (canRecoverFromExpiredToken && (await tryRecoverFromExpiredToken(store))) {
      // Don't show error or call next - we're retrying after token refresh
      return;
    }

    // Show error page and let the failure action reach the reducer so status
    // updates from PENDING to REJECTED
    store.dispatch(
      showGenericErrorNotification({
        action: action.type,
        error,
        errorCode: errorStatusCode ? `${errorStatusCode}` : undefined,
      }),
    );
    next(action);
  };
